/* ============================================================
 * AYU /termin/danke – Termin-Bestätigung Dankesseite
 * Design: Sacred Modern (Gold + Olive) – analog GENO /termin/danke
 * Sections: Hero, Video, Was dich erwartet, Testimonials,
 *           Demo CTA, Team, Book Store, Final CTA
 * ============================================================ */

import { useRef, useState, useEffect } from "react";
import { Link } from "wouter";
import { motion, useInView } from "framer-motion";
import { IMAGES, TESTIMONIALS } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import {
  CheckCircle,
  Mail,
  MessageCircle,
  Heart,
  Star,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Leaf,
  Clock,
  Play,
} from "lucide-react";

/* ---------- Animated Section Wrapper ---------- */
function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ---------- Team Data ---------- */
const TEAM = [
  {
    name: "Annam Hanfstein",
    role: "Gründerin & CEO AYU-rveda",
    image: IMAGES.annamBlue,
    description:
      "Ayurveda-Expertin und Autorin. 180.000+ Menschen begleitet auf dem Weg zu ganzheitlicher Gesundheit.",
    tags: ["Ayurveda", "Ernährung", "Ganzheitliche Gesundheit"],
  },
  {
    name: "Lilli",
    role: "Ernährungsberaterin & Mentalcoach",
    image:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/lilli-real_3821713a.png",
    description:
      "Zweifache Mama und leidenschaftlich sportbegeistert. Seit 2015 begleitet sie Menschen auf ihrem Weg zu mehr Gesundheit, Energie und nachhaltiger Balance.",
    tags: ["Ernährung", "Mentalcoaching", "Ganzheitlich"],
  },
  {
    name: "Silke",
    role: "Ganzheitliche Ernährungsberaterin & Coach",
    image:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/silke-cropped_1f8d8adc.png",
    description:
      "Ihre Vision ist es, Menschen auf ganzheitlicher Ebene zu unterstützen, damit sie ihr eigenes Körpergefühl zurückerlangen.",
    tags: ["Ernährung", "Coaching", "Körperbewusstsein"],
  },
  {
    name: "Susan",
    role: "Ernährungsberaterin & Gesundheitscoach",
    image:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/susan-betreuerin_f35687b6.png",
    description:
      "Durch ihre eigene Krebserkrankung hat Susan erfahren, wie wichtig bewusste Ernährung für unser Leben ist. Heute gibt sie ihr Wissen weiter.",
    tags: ["Ernährung", "Gesundheitscoaching", "Prävention"],
  },
];

/* ============================================================
 * MAIN PAGE COMPONENT
 * ============================================================ */
export default function TerminDanke() {
  const { t: translate } = useI18n();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[oklch(0.14_0.02_65)]">
      {/* ========== MINIMAL HEADER ========== */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[oklch(0.14_0.02_65/0.95)] backdrop-blur-xl shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="container flex items-center justify-between py-4">
          <Link href="/">
            <img src={IMAGES.logo} alt="AYU" className="h-10 w-auto" />
          </Link>
          <Link
            href="/"
            className="flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-light transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zur Homepage
          </Link>
        </div>
      </header>

      {/* ========== HERO: TERMIN BESTÄTIGT ========== */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Mandala Background */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url(${IMAGES.mandala})`,
            backgroundSize: "800px",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[oklch(0.14_0.02_65)]" />

        <div className="container relative z-10 text-center max-w-3xl mx-auto">
          <AnimatedSection>
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 rounded-full bg-[oklch(0.52_0.09_128/0.15)] flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-[oklch(0.52_0.09_128)]" />
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)] mb-6">
              Termin bestätigt
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-[oklch(0.95_0.02_85)] leading-tight mb-6">
              Danke, dass du den{" "}
              <span className="italic text-gold">ersten Schritt</span>{" "}
              gemacht hast.
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <p className="text-lg md:text-xl text-[oklch(0.75_0.02_85)] leading-relaxed mb-10 max-w-2xl mx-auto">
              Wir freuen uns auf dein persönliches Gespräch. In der
              Zwischenzeit möchten wir dir zeigen, was dich erwartet — und wer
              hinter AYU steht.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== VIDEO SECTION ========== */}
      <section className="pb-20">
        <div className="container max-w-3xl mx-auto">
          <AnimatedSection>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[oklch(0.78_0.11_85/0.15)]">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/IAs5BFEVzFA?rel=0&modestbranding=1"
                  title="AYU – Dein persönlicher Gesundheitsplan"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== WAS DICH ERWARTET (3 Cards) ========== */}
      <section className="py-20">
        <div className="container max-w-5xl mx-auto">
          <AnimatedSection>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.95_0.02_85)] mb-4">
              Kein Arzttermin. Ein{" "}
              <span className="italic text-gold">
                Gespräch auf Augenhöhe.
              </span>
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              {
                icon: Mail,
                title: "Bestätigung per E-Mail",
                text: "Du erhältst in wenigen Minuten eine E-Mail mit allen Details zu deinem Termin. Prüfe auch deinen Spam-Ordner.",
                color: "oklch(0.78_0.11_85)",
              },
              {
                icon: MessageCircle,
                title: "Bereite dich vor",
                text: "Überlege dir, welche Fragen du zu deiner Gesundheit hast. Je offener du bist, desto mehr können wir für dich tun.",
                color: "oklch(0.52_0.09_128)",
              },
              {
                icon: Heart,
                title: "Dein Gespräch",
                text: "Kein Verkaufsdruck. Kein Skript. Einfach ein ehrliches Gespräch über deine Gesundheit und was AYU für dich tun kann.",
                color: "oklch(0.65_0.15_25)",
              },
            ].map((card, i) => (
              <AnimatedSection key={card.title} delay={i * 0.15}>
                <div className="rounded-xl p-8 bg-[oklch(0.18_0.02_65)] border border-[oklch(0.78_0.11_85/0.1)] hover:border-[oklch(0.78_0.11_85/0.2)] transition-all h-full">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${card.color}20` }}
                  >
                    <card.icon
                      className="w-6 h-6"
                      style={{ color: card.color }}
                    />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[oklch(0.93_0.015_80)] mb-3">
                    {card.title}
                  </h3>
                  <p className="text-[oklch(0.65_0.02_85)] leading-relaxed">
                    {card.text}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Wichtig zu wissen */}
          <AnimatedSection delay={0.3}>
            <div className="mt-10 rounded-xl p-6 bg-[oklch(0.18_0.02_65)] border border-[oklch(0.52_0.09_128/0.15)] flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center shrink-0 mt-0.5">
                <Clock className="w-5 h-5 text-[oklch(0.52_0.09_128)]" />
              </div>
              <div>
                <h4 className="font-playfair text-lg font-bold text-[oklch(0.93_0.015_80)] mb-2">
                  Wichtig zu wissen
                </h4>
                <p className="text-[oklch(0.65_0.02_85)] leading-relaxed">
                  Dieses Gespräch ist für dich — nicht für uns. Wenn AYU nicht
                  das Richtige für dich ist, sagen wir dir das ehrlich. Unser
                  Ziel ist es, dass du mit Klarheit aus dem Gespräch gehst,
                  egal wie du dich entscheidest.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="py-20 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container max-w-5xl mx-auto">
          <AnimatedSection>
            <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)] mb-4">
              Echte Menschen. Echte Ergebnisse.
            </p>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.93_0.015_80)] mb-14">
              Was andere über{" "}
              <span className="italic text-gold">AYU</span> sagen
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.slice(0, 4).map((testimonial, i) => (
              <AnimatedSection key={testimonial.name} delay={i * 0.1}>
                <div className="rounded-xl p-8 bg-[oklch(0.18_0.02_65)] border border-[oklch(0.78_0.11_85/0.1)] h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star
                        key={j}
                        className="w-4 h-4 text-gold fill-gold"
                      />
                    ))}
                  </div>
                  <blockquote className="text-[oklch(0.8_0.02_85)] leading-relaxed mb-6 italic">
                    „{translate(testimonial.textKey as any)}"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[oklch(0.52_0.09_128/0.15)] flex items-center justify-center">
                      <span className="text-xs font-bold text-[oklch(0.52_0.09_128)]">
                        {testimonial.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[oklch(0.93_0.015_80)] text-sm">
                        {testimonial.name}
                      </p>
                      <p className="text-xs text-[oklch(0.55_0.02_85)]">
                        AYU-Nutzerin
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== DEMO CTA ========== */}
      <section className="py-20 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4">
              Jetzt ausprobieren
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.93_0.015_80)] mb-4">
              Teste AYU{" "}
              <span className="italic text-gold">kostenlos</span>
            </h2>
            <p className="text-lg text-[oklch(0.65_0.02_85)] leading-relaxed mb-10 max-w-xl mx-auto">
              Erlebe, wie sich ein personalisierter KI-Gesundheitscoach anfühlt
              — mit echten Beispieldaten von unserem Muster-Profil.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <Link
              href="/demo"
              className="inline-flex items-center gap-3 px-10 py-5 bg-gold text-[oklch(0.14_0.02_65)] font-bold text-lg rounded-xl hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <Play className="w-5 h-5" />
              AYU Demo starten
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex items-center justify-center gap-6 mt-5 text-sm text-[oklch(0.55_0.02_85)]">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Kostenlos
              </span>
              <span className="flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Keine Anmeldung nötig
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== TEAM SECTION ========== */}
      <section className="py-20 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container max-w-6xl mx-auto">
          <AnimatedSection>
            <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)] mb-4">
              Echte Menschen. Echtes Verständnis.
            </p>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.93_0.015_80)] mb-4">
              Dein{" "}
              <span className="italic text-gold">AYU-Team</span>
            </h2>
            <p className="text-center text-[oklch(0.65_0.02_85)] mb-14 max-w-2xl mx-auto">
              Du buchst keinen anonymen Termin. Du triffst Menschen, die
              zuhören, verstehen und dich ehrlich beraten.
            </p>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((person, i) => (
              <AnimatedSection key={person.name} delay={i * 0.12}>
                <div className="group rounded-2xl overflow-hidden bg-[oklch(0.18_0.02_65)] border border-[oklch(0.78_0.11_85/0.1)] hover:border-[oklch(0.78_0.11_85/0.2)] transition-all h-full">
                  <div className="relative overflow-hidden aspect-[4/5]">
                    <img
                      src={person.image}
                      alt={person.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[oklch(0.14_0.02_65/0.6)]" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-playfair text-lg font-bold text-[oklch(0.93_0.015_80)]">
                      {person.name}
                    </h3>
                    <p className="text-sm font-semibold text-gold mt-1 mb-3">
                      {person.role}
                    </p>
                    <p className="text-sm text-[oklch(0.65_0.02_85)] leading-relaxed mb-4">
                      {person.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {person.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full text-xs font-medium bg-[oklch(0.52_0.09_128/0.1)] text-[oklch(0.52_0.09_128)] border border-[oklch(0.52_0.09_128/0.15)]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BOOK STORE SECTION ========== */}
      <section className="py-20 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="rounded-2xl p-10 md:p-14 bg-[oklch(0.18_0.02_65)] border border-[oklch(0.78_0.11_85/0.1)]">
              <div className="md:flex items-center gap-10">
                <div className="flex-1 mb-8 md:mb-0">
                  <p className="text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4">
                    Bis zu deinem Termin
                  </p>
                  <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[oklch(0.93_0.015_80)] mb-4">
                    Tauche tiefer ein — mit unseren{" "}
                    <span className="italic text-gold">Büchern</span>
                  </h2>
                  <p className="text-[oklch(0.65_0.02_85)] leading-relaxed mb-8">
                    Erfahre mehr über personalisierte Gesundheit, Ayurveda und
                    wie du deinen Körper besser verstehen kannst. Perfekte
                    Vorbereitung auf dein Beratungsgespräch.
                  </p>
                  <Link
                    href="/buch"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-[oklch(0.14_0.02_65)] font-semibold rounded-lg hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    <BookOpen className="w-5 h-5" />
                    Zum Book Store
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-48 h-64 rounded-lg bg-gradient-to-br from-[oklch(0.78_0.11_85/0.2)] to-[oklch(0.52_0.09_128/0.2)] flex items-center justify-center border border-[oklch(0.78_0.11_85/0.15)]">
                    <div className="text-center">
                      <BookOpen className="w-12 h-12 text-gold mx-auto mb-3" />
                      <p className="text-sm font-bold text-gold">AYU</p>
                      <p className="text-xs text-[oklch(0.65_0.02_85)]">
                        E-Book & Print
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <Sparkles className="w-10 h-10 text-gold mx-auto mb-6" />
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[oklch(0.93_0.015_80)] mb-4">
              Der schwierigste Schritt ist der erste.
            </h2>
            <p className="text-lg text-[oklch(0.65_0.02_85)] leading-relaxed mb-10">
              Und den hast du gerade gemacht. Wir freuen uns auf dich — bis
              bald in deinem persönlichen Gespräch.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[oklch(0.78_0.11_85/0.2)] text-[oklch(0.85_0.02_85)] hover:bg-[oklch(0.78_0.11_85/0.08)] transition-all font-medium"
              >
                <Leaf className="w-4 h-4" />
                Erfahrungen lesen
              </Link>
              <Link
                href="/buch"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[oklch(0.78_0.11_85/0.2)] text-[oklch(0.85_0.02_85)] hover:bg-[oklch(0.78_0.11_85/0.08)] transition-all font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Bücher entdecken
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-8 border-t border-[oklch(0.78_0.11_85/0.08)]">
        <div className="container text-center">
          <img
            src={IMAGES.logo}
            alt="AYU"
            className="h-8 w-auto mx-auto mb-4 opacity-60"
          />
          <div className="flex items-center justify-center gap-6 text-xs text-[oklch(0.45_0.02_85)]">
            <Link
              href="/impressum"
              className="hover:text-[oklch(0.78_0.11_85)] transition-colors"
            >
              Impressum
            </Link>
            <Link
              href="/datenschutz"
              className="hover:text-[oklch(0.78_0.11_85)] transition-colors"
            >
              Datenschutz
            </Link>
            <Link
              href="/agb"
              className="hover:text-[oklch(0.78_0.11_85)] transition-colors"
            >
              AGB
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
